import { MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";

/** An undo batch paired with the line it belongs to. One save can move the plan
 *  on several lines, and undo is per line, so the id alone is not addressable. */
export interface PlanChangeRef {
	assetId: number;
	planChangeId: number;
}

/** Shared by every SP that can create undo batches: they all return the pairs
 *  as their last result set. */
export const toPlanChangeRefs = (rows: unknown[]): PlanChangeRef[] =>
	(rows as { AssetId: number; PlanChangeId: number }[])
		.filter((r) => r.AssetId != null && r.PlanChangeId != null)
		.map((r) => ({
			assetId: Number(r.AssetId),
			planChangeId: Number(r.PlanChangeId),
		}));

export interface PlanChange {
	Kind: "Undo" | "Redo";
	PlanChangeId: number;
	Source: string;
	CreatedAt: string;
	CreatedBy: string | null;
	MovedCount: number;
}

export interface PlanChangeCandidates {
	undo: PlanChange | null;
	redo: PlanChange | null;
}

export const getLastPlanChange = async (
	assetId: number,
	signal?: AbortSignal,
): Promise<PlanChangeCandidates> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "AssetId", value: assetId }];
	const resp = await apiService.callV2(
		"[FAB].[GetLastPlanChange]",
		parameters,
		signal,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching last plan change");
	}

	const rows = get(resp, "data.tables[0].rows", []) as PlanChange[];
	return {
		undo: rows.find((r) => r.Kind === "Undo") ?? null,
		redo: rows.find((r) => r.Kind === "Redo") ?? null,
	};
};

export const undoPlanChange = async (assetId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "AssetId", value: assetId }];
	const resp = await apiService.callV2("[FAB].[UndoPlanChange]", parameters);

	if (!resp.ok) {
		throw new Error(resp.message || "Error undoing plan change");
	}
};

export const redoPlanChange = async (assetId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "AssetId", value: assetId }];
	const resp = await apiService.callV2("[FAB].[RedoPlanChange]", parameters);

	if (!resp.ok) {
		throw new Error(resp.message || "Error redoing plan change");
	}
};
